import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ResendInvitationParams {
  inviteId: string;
}

interface DeleteInvitationParams {
  inviteId: string;
}

export const useInvitationActions = () => {
  const queryClient = useQueryClient();

  const resendInvitation = useMutation({
    mutationFn: async ({ inviteId }: ResendInvitationParams) => {
      const { data, error } = await supabase.functions.invoke('workspace-invitations', {
        body: {
          action: 'resend',
          invite_id: inviteId
        }
      });

      if (error) throw error;
      if (!data.ok) throw new Error(data.error || 'Failed to resend invitation');
      
      return data;
    },
    onSuccess: (data) => {
      toast.success('Invitation resent successfully');
      // Invalidate workspace members query to refresh the invitations list
      queryClient.invalidateQueries({ queryKey: ['workspace-members'] });
    },
    onError: (error: any) => {
      console.error('Error resending invitation:', error);
      toast.error('Failed to resend invitation: ' + (error.message || 'Unknown error'));
    }
  });

  const deleteInvitation = useMutation({
    mutationFn: async ({ inviteId }: DeleteInvitationParams) => {
      const { data, error } = await supabase.functions.invoke('workspace-invitations', {
        body: {
          action: 'delete',
          invite_id: inviteId
        }
      });

      if (error) throw error;
      if (!data.ok) throw new Error(data.error || 'Failed to delete invitation');
      
      return data;
    },
    onSuccess: (data) => {
      toast.success('Invitation deleted successfully');
      // Invalidate workspace members query to refresh the invitations list
      queryClient.invalidateQueries({ queryKey: ['workspace-members'] });
    },
    onError: (error: any) => {
      console.error('Error deleting invitation:', error);
      toast.error('Failed to delete invitation: ' + (error.message || 'Unknown error'));
    }
  });

  return {
    resendInvitation,
    deleteInvitation,
    isResending: resendInvitation.isPending,
    isDeleting: deleteInvitation.isPending
  };
};